<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useCveStore } from "../store/cveStore";
import type { CveFinding, RepoDetail } from "../types";

const store = useCveStore();
const snackbar = ref(false);
const snackbarText = ref("");
const snackbarColor = ref("success");
const expanded = ref<string[]>([]);
const detailLoading = ref<Record<string, boolean>>({});
const detailCache = ref<Record<string, RepoDetail>>({});

const severityColor: Record<string, string> = {
  CRITICAL: "error",
  HIGH: "warning",
  MEDIUM: "orange",
  LOW: "info",
  NONE: "grey",
};

const headers = [
  { title: "", key: "data-table-expand", sortable: false, width: 48 },
  { title: "Service", key: "serviceName", sortable: true },
  { title: "Language", key: "language", sortable: true },
  { title: "Severity", key: "maxSeverity", sortable: true },
  { title: "CVEs", key: "cveCount", sortable: true },
  { title: "Audit", key: "auditStatus", sortable: true },
  { title: "Last scanned", key: "lastScannedAt", sortable: true },
  { title: "Actions", key: "actions", sortable: false },
];

const findingHeaders = [
  { title: "Advisory", key: "advisoryId" },
  { title: "Package", key: "package" },
  { title: "Version", key: "version" },
  { title: "CVSS", key: "cvss" },
  { title: "Severity", key: "severity" },
  { title: "Ecosystem", key: "ecosystem" },
];

const isJobRunning = computed(() => (serviceName: string) => serviceName in store.activeJobs);

onMounted(async () => {
  await store.loadDashboard(true);
});

watch(expanded, (newExpanded) => {
  for (const serviceName of newExpanded) {
    void handleExpand(serviceName, true);
  }
});

async function handleRefresh(): Promise<void> {
  detailCache.value = {};
  expanded.value = [];
  await store.loadDashboard(true);
}

async function handleExpand(serviceName: string, isExpanded: boolean): Promise<void> {
  if (!isExpanded || detailCache.value[serviceName]) {
    return;
  }
  detailLoading.value[serviceName] = true;
  try {
    detailCache.value[serviceName] = await store.loadRepoDetail(serviceName);
  } catch (err) {
    snackbarText.value = err instanceof Error ? err.message : "Failed to load CVE details";
    snackbarColor.value = "error";
    snackbar.value = true;
  } finally {
    detailLoading.value[serviceName] = false;
  }
}

function formatDate(value: string | null): string {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleString();
}

async function handleOpenMr(serviceName: string, existingMrUrl: string | null): Promise<void> {
  if (existingMrUrl) {
    window.open(existingMrUrl, "_blank", "noopener,noreferrer");
    return;
  }
  try {
    const mrUrl = await store.openMr(serviceName);
    if (mrUrl) {
      snackbarText.value = `MR opened for ${serviceName}`;
      snackbarColor.value = "success";
      window.open(mrUrl, "_blank", "noopener,noreferrer");
    } else {
      snackbarText.value = `Remediation queued for ${serviceName}`;
      snackbarColor.value = "info";
    }
  } catch (err) {
    snackbarText.value = err instanceof Error ? err.message : "Failed to open MR";
    snackbarColor.value = "error";
  }
  snackbar.value = true;
}
</script>

<template>
  <v-container fluid class="pa-6">
    <v-row align="center" class="mb-2">
      <v-col cols="12" md="8">
        <h1 class="text-h4 mb-1">Amb CVE Assistant</h1>
        <p class="text-body-2 text-medium-emphasis">
          OWASP dependency-check across {{ store.summary.repos }} ambulatory repos
        </p>
      </v-col>
      <v-col cols="12" md="4" class="text-md-end">
        <v-btn color="primary" :loading="store.scanning" @click="handleRefresh"> Refresh scan </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col cols="6" sm="3">
        <v-card variant="tonal" color="error">
          <v-card-text class="text-center">
            <div class="text-h5">{{ store.summary.totalCves }}</div>
            <div class="text-caption">Total CVEs</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="tonal" color="warning">
          <v-card-text class="text-center">
            <div class="text-h5">{{ store.summary.failing }}</div>
            <div class="text-caption">Audit failed</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="tonal" color="success">
          <v-card-text class="text-center">
            <div class="text-h5">{{ store.summary.passing }}</div>
            <div class="text-caption">Audit passing</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="tonal">
          <v-card-text class="text-center">
            <div class="text-h5">{{ store.summary.unknown }}</div>
            <div class="text-caption">Not scanned</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="store.needsGitLabToken" type="warning" class="mb-4" variant="tonal">
      GitLab is not configured. Set <code>GITLAB_TOKEN</code> in <code>amb-cve-assistant/.env</code>, or run
      <code>glab auth login --hostname git.getwellnetwork.com</code>, then restart the API and click Refresh scan.
    </v-alert>

    <v-alert v-if="store.error" type="error" class="mb-4">{{ store.error }}</v-alert>

    <v-data-table
      v-model:expanded="expanded"
      :headers="headers"
      :items="store.rows"
      :loading="store.loading || store.scanning"
      item-key="serviceName"
      show-expand
      class="elevation-1"
    >
      <template #item.maxSeverity="{ item }">
        <v-chip :color="severityColor[item.maxSeverity] || 'grey'" size="small" label>
          {{ item.maxSeverity }}
        </v-chip>
      </template>

      <template #item.auditStatus="{ item }">
        <v-chip
          :color="item.auditStatus === 'success' ? 'success' : item.auditStatus === 'failed' ? 'error' : 'grey'"
          size="small"
          variant="tonal"
        >
          {{ item.auditStatus }}
        </v-chip>
      </template>

      <template #item.lastScannedAt="{ item }">
        {{ formatDate(item.lastScannedAt) }}
      </template>

      <template #item.actions="{ item }">
        <v-btn
          size="small"
          color="primary"
          variant="flat"
          :loading="isJobRunning(item.serviceName)"
          :disabled="item.auditStatus === 'success'"
          @click="handleOpenMr(item.serviceName, item.mrUrl)"
        >
          {{ item.mrUrl ? "View MR" : "Open MR" }}
        </v-btn>
      </template>

      <template #expanded-row="{ columns, item }">
        <tr>
          <td :colspan="columns.length">
            <v-progress-linear v-if="detailLoading[item.serviceName]" indeterminate class="mb-2" />
            <div v-else-if="detailCache[item.serviceName]?.findings?.length" class="pa-4">
              <v-data-table
                :headers="findingHeaders"
                :items="detailCache[item.serviceName].findings as CveFinding[]"
                density="compact"
                hide-default-footer
              >
                <template #item.severity="{ item: finding }">
                  <v-chip :color="severityColor[finding.severity] || 'grey'" size="x-small" label>
                    {{ finding.severity }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
            <div v-else class="pa-4 text-medium-emphasis">
              No CVE findings — audit may be passing or not yet scanned.
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>
